import React from "react";
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Eye,
  Crosshair,
  Loader2,
  MapPin,
  Clock,
  Flag,
  Trash2,
  X,
  AlertTriangle,
  Check,
  Accessibility,
  PackageOpen,
  Bike,
  Car,
  Info,
  Navigation,
  Users,
  Warehouse,
  ArrowRight,
  UserCog,
  Ban,
  Satellite,
  Map as MapIcon,
  Share2,
  MoreHorizontal,
  ChevronRight,
  UserPlus,
} from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { getDeviceId } from "../lib/deviceId";
import { isProfileComplete, getProfile } from "../lib/driverProfile";
import { shareReferral, INVITE_FF_MESSAGE } from "../lib/referral";
import DriverIdentityModal from "../components/DriverIdentityModal";
import CarLocationSheet from "../components/CarLocationSheet";
import HandoverPanel from "../components/HandoverPanel";
import CampaignCard from "../components/CampaignCard";
import GroupsSheet from "../components/GroupsSheet";
import MapLayersControl from "../components/MapLayersControl";
import NearbyParkingSheet from "../components/NearbyParkingSheet";
import Onboarding from "../components/Onboarding";
import CoachMarks from "../components/CoachMarks";
import ParkingMemoryPrompt from "../components/ParkingMemoryPrompt";
import {
  onboardingSeen,
  markOnboardingSeen,
  coachMarksSeen,
  markCoachMarksSeen,
  canShowParkingPrompt,
  markParkingPromptShown,
  markParkingPromptDismissed,
  markParkingPromptAccepted,
} from "../lib/onboarding";
import { getCarLocation, buildNavigationUrl } from "../lib/carLocation";
import { readTimer, secondsLeft, formatLeftShort, syncTimer } from "../lib/parkingTimer";
import { errorMessage, errorReason } from "../lib/apiError";
import ParkingPlacesLayer from "../components/ParkingPlacesLayer";
import DestinationPill from "../components/DestinationPill";
import DestinationSheet from "../components/DestinationSheet";
import JourneyCard from "../components/JourneyCard";
import {
  loadJourney,
  saveJourney,
  clearJourney,
  penalizePlace,
  buildNavAppUrl,
  navTarget,
} from "../lib/journey";
import { loadLayers, saveLayers, PLACE_LAYER_KINDS } from "../lib/mapLayers";
import { track } from "../lib/analytics";
import PrivateParkingSheet from "../components/PrivateParkingSheet";
import PrivateListingCard from "../components/PrivateListingCard";
import QuickSpotSheet from "../components/QuickSpotSheet";
import ReportParkingSheet from "../components/ReportParkingSheet";
import { fetchListings, resizePhoto } from "../lib/privateParking";
import { trackActivation } from "../lib/activation";
import { markEngaged } from "../lib/push";
import { fetchGroupOverlay, sendLiveBeat } from "../lib/groups";
import { formatRelativeTime } from "../lib/carLocation";

const MALTA_CENTER = [35.9375, 14.3754];
const EMPTY_SPOTS = [];
// Hard viewport guard: a slow tile load or a stray pinch can never reach a world view.
const MALTA_MAX_BOUNDS = [
  [35.62, 13.95],
  [36.22, 14.88],
];
const POLL_MS = 20000;
const NEARBY_RADIUS_M = 25000;  // ~all of Malta + Gozo, so bad GPS still sees spots
const GOOD_ACCURACY_M = 60;     // below this we trust the fix and snap the map
const WARN_ACCURACY_M = 150;    // above this we surface a warning to the user
const FAR_PIN_WARN_M = 200;     // moved pin distance from user → "far from you" warning
const FAR_PIN_BLOCK_M = 2000;   // beyond this → block publish

// "Did you find parking?" is only plausible once the driver has had time to
// actually drive there. It is never treated as proof of arrival: the question
// still has to be answered, and "I parked" is available the whole time.
const ASK_MIN_MS = 90 * 1000;
const askReady = (j) => Date.now() - (j?.directions_at || j?.started_at || 0) >= ASK_MIN_MS;


const TYPE_META = {
  normal: { label: "Normal", short: "Normal", color: "#22c55e", icon: <Car className="w-4 h-4" /> },
  blue_badge: { label: "Blue Badge only", short: "Blue Badge", color: "#3b82f6", icon: <Accessibility className="w-4 h-4" /> },
  loading_bay: { label: "Loading bay", short: "Loading", color: "#f59e0b", icon: <PackageOpen className="w-4 h-4" /> },
  motorcycle: { label: "Motorcycle", short: "Moto", color: "#a855f7", icon: <Bike className="w-4 h-4" /> },
};

const DURATIONS = [5, 10, 15];

// CARTO requires a (free) key for direct raster basemap requests; without it the
// tiles carry an "API KEY REQUIRED" watermark. The key is never hardcoded: it comes
// from the build-time env var, or at runtime from /api/map-config (CARTO_API_KEY)
// so it can be set without rebuilding the frontend.
const CARTO_VOYAGER_BASE = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const cartoUrlFor = (key) => (key ? `${CARTO_VOYAGER_BASE}?key=${encodeURIComponent(key)}` : CARTO_VOYAGER_BASE);

/** Clears an external boolean flag on any user map interaction (pan/zoom/click).
    Used to auto-hide the soft profile hint once the user starts moving.
    The coach-mark callback is only fired for genuinely user-initiated gestures
    (click + dragstart) so the initial programmatic pan-to-user doesn't dismiss it. */
function ClearOnMapInteract({ clearHint, clearCoach }) {
  useMapEvents({
    click: () => { clearHint(); clearCoach && clearCoach(); },
    movestart: () => clearHint(),
    zoomstart: () => clearHint(),
    dragstart: () => clearCoach && clearCoach(),
  });
  return null;
}

/* Tap-to-move: when we're in draft mode, tapping the map moves the pin to that spot.
   Reliable on mobile even when drag-to-move fails or the pin sits behind the sheet. */
function DraftTapMove({ enabled, onMove }) {
  useMapEvents({
    click: (e) => {
      if (!enabled) return;
      onMove({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

/* Pans the map so the draft pin is centred in the visible area ABOVE the
   bottom sheet (which covers roughly the lower 40% of the viewport on mobile). */
function PanToDraft({ pinPos }) {
  const map = useMap();
  const lastRef = React.useRef(null);
  React.useEffect(() => {
    if (!pinPos) { lastRef.current = null; return; }
    // Only pan on first entry into draft mode, not on every drag.
    const key = "on";
    if (lastRef.current === key) return;
    lastRef.current = key;
    const size = map.getSize();
    // Shift the target UP by ~20% of viewport so the pin appears above the sheet.
    const offsetY = Math.round(size.y * 0.2);
    const point = map.project([pinPos.lat, pinPos.lng], map.getZoom());
    point.y -= offsetY;
    const target = map.unproject(point, map.getZoom());
    map.flyTo(target, Math.max(map.getZoom(), 17), { duration: 0.5 });
  }, [pinPos, map]);
  return null;
}

/* Remembers the last valid viewport so a slow start never shows a world view. */
const VIEW_KEY = "spotdrop_map_view_v1";

function readSavedView() {
  try {
    const v = JSON.parse(localStorage.getItem(VIEW_KEY) || "null");
    if (v && Number.isFinite(v.lat) && Number.isFinite(v.lng) && v.zoom >= 10) return v;
  } catch {
    /* ignore */
  }
  return null;
}

function ViewPersist() {
  const map = useMap();
  React.useEffect(() => {
    const save = () => {
      const c = map.getCenter();
      const zoom = map.getZoom();
      if (zoom < 10) return;
      try {
        localStorage.setItem(VIEW_KEY, JSON.stringify({ lat: c.lat, lng: c.lng, zoom }));
      } catch {
        /* ignore */
      }
    };
    map.on("moveend", save);
    return () => map.off("moveend", save);
  }, [map]);
  return null;
}

/* Exposes the Leaflet map instance to the page (used by the Nearby parking sheet). */
function MapRefBridge({ refObj }) {
  const map = useMap();
  React.useEffect(() => {
    refObj.current = map;
  }, [map, refObj]);
  return null;
}

/* ---------- Custom pin icons ---------- */
/* Icons are cached: identical spot states reuse the same L.divIcon instead of
   rebuilding HTML on every render. */
const pinCache = new Map();

function spotsSignature(list) {
  return (list || [])
    .map(
      (s) =>
        `${s.id}:${s.claimed ? 1 : 0}:${s.claimed_by_me ? 1 : 0}:${s.report_count || 0}:${
          s.possibly_occupied ? 1 : 0
        }:${s.handover_state || ""}:${s.has_photo ? 1 : 0}:${s.expires_at}`
    )
    .join("|");
}

function buildPin(spot) {
  const cacheKey = `${spot.parking_type}|${spot.duration_min}|${spot.claimed ? 1 : 0}|${spot.mine ? 1 : 0}`;
  const cached = pinCache.get(cacheKey);
  if (cached) return cached;
  const meta = TYPE_META[spot.parking_type] || TYPE_META.normal;
  const color = meta.color;
  const ring = spot.mine ? "#ffffff" : "rgba(255,255,255,0.85)";
  const badge = spot.claimed
    ? `<div style="position:absolute;top:-6px;right:-8px;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:#f59e0b;color:#0a0a0c;font:700 10px 'Manrope',sans-serif;display:flex;align-items:center;justify-content:center;border:2px solid #fff;">→</div>`
    : "";
  const html = `
    <div style="position:relative;display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
      <div style="position:relative;">
        <div style="position:absolute;inset:-6px;border-radius:50%;background:${color};opacity:0.28;filter:blur(6px);"></div>
        <div style="position:relative;width:28px;height:28px;border-radius:50%;background:${color};border:3px solid ${ring};box-shadow:0 4px 12px rgba(0,0,0,0.4);"></div>
        ${badge}
      </div>
      <div style="margin-top:4px;padding:2px 7px;border-radius:8px;background:rgba(10,10,12,0.92);color:#fff;font:700 11px 'Manrope',sans-serif;letter-spacing:-0.01em;white-space:nowrap;border:1px solid rgba(255,255,255,0.12);box-shadow:0 2px 6px rgba(0,0,0,0.3);">${meta.short} · ${spot.duration_min}m</div>
    </div>`;
  const icon = L.divIcon({
    html,
    className: "",
    iconSize: [80, 56],
    iconAnchor: [40, 14],
  });
  pinCache.set(cacheKey, icon);
  return icon;
}

function userIcon(accuracyHigh) {
  const color = accuracyHigh ? "#f59e0b" : "#3b82f6";
  return L.divIcon({
    html: `<div style="position:relative;">
      <div style="position:absolute;inset:-12px;border-radius:50%;background:${color}33;"></div>
      <div style="position:relative;width:16px;height:16px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.6);"></div>
    </div>`,
    className: "",
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/* Friends & Family markers — visually distinct from parking spots (cyan,
   square badge with the member's nickname) so they can't be confused. */
function friendIcon(nickname) {
  const label = String(nickname || "Driver").slice(0, 14);
  return L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="position:relative;width:20px;height:20px;border-radius:6px;background:#22d3ee;border:3px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.45);"></div>
      <div style="margin-top:4px;padding:2px 7px;border-radius:8px;background:rgba(10,10,12,0.92);color:#67e8f9;font:700 11px 'Manrope',sans-serif;white-space:nowrap;border:1px solid rgba(34,211,238,0.4);">${label}</div>
    </div>`,
    className: "",
    iconSize: [80, 48],
    iconAnchor: [40, 10],
  });
}

function friendCarIcon(nickname) {
  const label = `${String(nickname || "Driver").slice(0, 12)}'s car`;
  return L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="width:20px;height:20px;border-radius:6px;background:rgba(10,10,12,0.92);border:2px solid #22d3ee;display:flex;align-items:center;justify-content:center;color:#67e8f9;font:700 11px 'Manrope',sans-serif;">P</div>
      <div style="margin-top:4px;padding:2px 7px;border-radius:8px;background:rgba(10,10,12,0.92);color:#a5f3fc;font:700 10px 'Manrope',sans-serif;white-space:nowrap;border:1px solid rgba(34,211,238,0.3);">${label}</div>
    </div>`,
    className: "",
    iconSize: [80, 46],
    iconAnchor: [40, 10],
  });
}

/* Private parking listing marker — deliberately different from live spot pins:
   dark plate with a "P" badge and the asking price. */
function privateIcon(listing) {
  const label = `RENT €${listing.price}${listing.period === "hourly" ? "/h" : listing.period === "daily" ? "/d" : "/m"}`;
  const dim = listing.available ? "" : "opacity:0.55;";
  return L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;${dim}">
      <div style="width:26px;height:26px;border-radius:7px;background:#111827;border:2px solid #ccff00;display:flex;align-items:center;justify-content:center;color:#ccff00;font:800 13px 'Manrope',sans-serif;box-shadow:0 4px 12px rgba(0,0,0,0.45);">€</div>
      <div style="margin-top:3px;padding:2px 7px;border-radius:8px;background:rgba(10,10,12,0.92);color:#ccff00;font:800 9px 'Manrope',sans-serif;letter-spacing:0.04em;white-space:nowrap;border:1px solid rgba(204,255,0,0.35);">${label}</div>
    </div>`,
    className: "",
    iconSize: [78, 50],
    iconAnchor: [39, 13],
  });
}

/* Destination flag for an active Parking Journey. */
function destinationIcon(name) {
  const label = String(name || "Destination").slice(0, 18);
  return L.divIcon({
    html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
      <div style="width:22px;height:22px;border-radius:50%;background:#0a0a0c;border:2px solid #ccff00;display:flex;align-items:center;justify-content:center;color:#ccff00;font:800 11px 'Manrope',sans-serif;box-shadow:0 4px 12px rgba(0,0,0,0.5);">◎</div>
      <div style="margin-top:3px;padding:2px 7px;border-radius:8px;background:rgba(10,10,12,0.92);color:#ccff00;font:700 10px 'Manrope',sans-serif;white-space:nowrap;border:1px solid rgba(204,255,0,0.35);">${label}</div>
    </div>`,
    className: "",
    iconSize: [80, 46],
    iconAnchor: [40, 11],
  });
}

/* ---------- Geolocation hook ---------- */
function useGeolocation() {
  const [pos, setPos] = React.useState(null);  // {lat,lng,acc}
  const [error, setError] = React.useState(null);
  const [granted, setGranted] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const watchIdRef = React.useRef(null);

  const start = React.useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError("Geolocation not supported on this device.");
      return;
    }
    setRefreshing(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy });
        setGranted(true);
        setError(null);
        setRefreshing(false);
      },
      (e) => { setError(e.message || "Could not get location."); setRefreshing(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        // Always update — even if accuracy is bad — so the warning can react,
        // but the caller decides whether to snap the map.
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy });
        setGranted(true);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  }, []);

  const refresh = React.useCallback(() => start(), [start]);

  React.useEffect(() => () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
  }, []);

  return { pos, error, granted, refresh, refreshing, start };
}

/* ---------- Map controls ---------- */
function RecenterButton({ pos, onRefresh, refreshing }) {
  const map = useMap();
  return (
    <button
      type="button"
      onClick={() => {
        if (pos) map.flyTo([pos.lat, pos.lng], 17, { duration: 0.6 });
        onRefresh && onRefresh();
      }}
      className="absolute right-3 z-[1600] w-12 h-12 rounded-full bg-[#141416] border border-white/15 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
      style={{ bottom: "calc(var(--journey-card-h, 0px) + env(safe-area-inset-bottom, 0px) + 7rem)" }}
      data-testid="recenter-btn"
      aria-label="Refresh location and recenter"
    >
      {refreshing
        ? <Loader2 className="w-5 h-5 text-[#ccff00] animate-spin" />
        : <Crosshair className="w-5 h-5 text-[#ccff00]" />}
    </button>
  );
}

/* Standard ⇄ Satellite map style toggle. Preference persisted in localStorage. */
function MapStyleToggle({ style, onChange }) {
  const isSat = style === "satellite";
  return (
    <button
      type="button"
      onClick={() => onChange(isSat ? "standard" : "satellite")}
      className="absolute right-3 z-[1600] w-12 h-12 rounded-full bg-[#141416] border border-white/15 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
      style={{ bottom: "calc(var(--journey-card-h, 0px) + env(safe-area-inset-bottom, 0px) + 11rem)" }}
      data-testid="map-style-toggle"
      aria-label={isSat ? "Switch to standard map" : "Switch to satellite view"}
    >
      {isSat
        ? <MapIcon className="w-5 h-5 text-[#ccff00]" data-testid="map-style-icon-standard" />
        : <Satellite className="w-5 h-5 text-[#ccff00]" data-testid="map-style-icon-satellite" />}
    </button>
  );
}

function ZoomTo({ pos }) {
  const map = useMap();
  const didInit = React.useRef(false);
  React.useEffect(() => {
    if (!pos || didInit.current) return;
    // Wait for an accurate-enough fix before snapping the map, so users
    // don't see themselves "in the sea" on the first low-quality fix.
    if (pos.acc != null && pos.acc > GOOD_ACCURACY_M) return;
    map.setView([pos.lat, pos.lng], 16);
    didInit.current = true;
  }, [pos, map]);
  return null;
}

/* Floating "Show my spot" button — only renders when the user has an active spot. */
function MySpotButton({ mySpot, markerRefs }) {
  const map = useMap();
  if (!mySpot) return null;
  const onClick = () => {
    map.flyTo([mySpot.lat, mySpot.lng], 17, { duration: 0.7 });
    const m = markerRefs.current?.[mySpot.id];
    if (m && typeof m.openPopup === "function") {
      setTimeout(() => m.openPopup(), 700);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 z-[1600] h-12 px-4 rounded-full bg-[#0a0a0c]/95 border border-[#ccff00]/40 text-[#ccff00] font-semibold inline-flex items-center gap-2 text-sm shadow-[0_4px_16px_rgba(0,0,0,0.4)] active:scale-95 transition-transform"
      style={{ bottom: "calc(var(--journey-card-h, 0px) + env(safe-area-inset-bottom, 0px) + 3rem)" }}
      data-testid="show-my-spot-btn"
    >
      <Navigation className="w-4 h-4" />
      Show my spot
    </button>
  );
}

/* ---------- Add-spot sheet (Stage 1: type & duration) ---------- */
function AddSpotSheet({ open, onClose, pos, onContinue }) {
  const [type, setType] = React.useState("normal");
  const [dur, setDur] = React.useState(5);
  const [waitMode, setWaitMode] = React.useState("waiting");

  React.useEffect(() => {
    if (open) { setType("normal"); setDur(5); setWaitMode("waiting"); }
  }, [open]);

  const next = (e) => {
    e.preventDefault();
    if (!pos) { toast.error("Waiting for your location…"); return; }
    onContinue({ parking_type: type, duration_min: dur, wait_mode: waitMode });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/60"
            onClick={onClose}
          />
          <motion.form
            onSubmit={next}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[2001] bg-[#141416] border-t border-white/10 rounded-t-3xl p-5 pb-7"
            style={{ paddingBottom: "calc(1.75rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="add-spot-sheet"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">Drop a spot · step 1 of 2</div>
                <div className="font-heading text-xl font-semibold text-white mt-1">I'm leaving</div>
              </div>
              <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Parking type</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TYPE_META).map(([k, m]) => {
                const active = type === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setType(k)}
                    className={`px-3 py-3 rounded-xl border flex items-center gap-2 text-sm transition-all ${
                      active
                        ? (k === "blue_badge"
                            ? "bg-blue-500/15 border-blue-400 text-blue-200"
                            : "bg-[#ccff00] border-[#ccff00] text-[#0a0a0c] font-semibold")
                        : "bg-[#0a0a0c] border-white/10 text-zinc-300"
                    }`}
                    data-testid={`type-${k}`}
                  >
                    <span style={{ color: active && k !== "blue_badge" ? "#0a0a0c" : TYPE_META[k].color }}>
                      {m.icon}
                    </span>
                    {m.label}
                  </button>
                );
              })}
            </div>

            {type === "blue_badge" && (
              <div className="mt-3 p-2.5 rounded-lg bg-blue-500/10 border border-blue-400/30 flex items-start gap-2 text-[11px] text-blue-200">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Only authorised Blue Badge holders may use this bay. Misuse is illegal.</span>
              </div>
            )}

            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Leaving in</div>
            <div className="grid grid-cols-3 gap-2">
              {DURATIONS.map((m) => (
                <button
                  key={m} type="button" onClick={() => setDur(m)}
                  className={`h-12 rounded-xl border transition-all ${
                    dur === m ? "bg-[#ccff00] border-[#ccff00] text-[#0a0a0c]" : "bg-[#0a0a0c] border-white/10 text-zinc-400"
                  }`}
                  data-testid={`dur-${m}`}
                >
                  <span className="text-sm font-bold">{m}</span><span className="text-[10px] uppercase ml-0.5 opacity-80">min</span>
                </button>
              ))}
            </div>

            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Can you wait for the arriving driver?</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setWaitMode("waiting")}
                className={`h-14 rounded-xl border text-left px-3 py-2 transition-all ${
                  waitMode === "waiting"
                    ? "bg-[#ccff00] border-[#ccff00] text-[#0a0a0c]"
                    : "bg-[#0a0a0c] border-white/10 text-zinc-300"
                }`}
                data-testid="wait-mode-waiting"
              >
                <div className="text-sm font-bold leading-tight">I can wait</div>
                <div className={`text-[10px] mt-0.5 ${waitMode === "waiting" ? "text-[#0a0a0c]/70" : "text-zinc-500"}`}>Up to 5 min</div>
              </button>
              <button
                type="button"
                onClick={() => setWaitMode("no_wait")}
                className={`h-14 rounded-xl border text-left px-3 py-2 transition-all ${
                  waitMode === "no_wait"
                    ? "bg-amber-500/15 border-amber-500 text-amber-100"
                    : "bg-[#0a0a0c] border-white/10 text-zinc-300"
                }`}
                data-testid="wait-mode-no-wait"
              >
                <div className="text-sm font-bold leading-tight">I can&apos;t wait</div>
                <div className={`text-[10px] mt-0.5 ${waitMode === "no_wait" ? "text-amber-200/80" : "text-zinc-500"}`}>Leaving right now</div>
              </button>
            </div>

            <button
              type="submit" disabled={!pos}
              className="mt-5 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="confirm-drop"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>

            <div className="mt-3 text-[10px] text-zinc-500 text-center leading-relaxed px-3">
              Next: drag the pin to the exact parking spot, then publish.
            </div>
          </motion.form>
        </>
      )}
    </AnimatePresence>
  );
}

/* ---------- Stage 2: drag pin to confirm location ---------- */
function ConfirmLocationBar({ open, draft, userPos, pinPos, onConfirm, onCancel, publishing }) {
  if (!open || !draft) return null;
  const distFromUser = userPos && pinPos
    ? Math.round(_haversine_m_client(userPos.lat, userPos.lng, pinPos.lat, pinPos.lng))
    : 0;
  const lowAcc = userPos?.acc != null && userPos.acc > WARN_ACCURACY_M;
  const tooFar = distFromUser > FAR_PIN_BLOCK_M;
  const farWarn = distFromUser > FAR_PIN_WARN_M && !tooFar;
  const meta = TYPE_META[draft.parking_type] || TYPE_META.normal;

  return (
    <motion.div
      initial={{ y: 240 }} animate={{ y: 0 }} exit={{ y: 240 }}
      transition={{ type: "spring", damping: 30, stiffness: 280 }}
      className="fixed inset-x-0 bottom-0 z-[2001] bg-[#141416] border-t border-white/10 rounded-t-3xl p-4 pb-7"
      data-testid="confirm-location-bar"
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">Step 2 of 2 · confirm</div>
          <div className="font-heading text-lg font-semibold text-white mt-0.5 flex items-center gap-2">
            <span style={{ color: meta.color }}>{meta.icon}</span>
            {meta.label} · {draft.duration_min} min
          </div>
        </div>
        <button type="button" onClick={onCancel} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center" data-testid="cancel-confirm">
          <X className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      <p className="text-[12px] text-zinc-300 leading-snug">
        Is this the actual parking spot? <span className="text-white font-semibold">Drag the pin</span> or <span className="text-white font-semibold">tap the map</span> to move it to the exact spot before publishing.
      </p>

      <div className="mt-2.5 text-[11px] text-zinc-400 flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5 text-[#ccff00]" />
        <span>{distFromUser} m from your location</span>
      </div>

      {lowAcc && (
        <div className="mt-2 p-2 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-100 text-[11px] flex items-start gap-1.5" data-testid="confirm-low-acc">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Your location accuracy is low (±{Math.round(userPos.acc)} m). Please check the pin location before publishing.
        </div>
      )}
      {tooFar && (
        <div className="mt-2 p-2 rounded-lg bg-red-500/15 border border-red-500/40 text-red-200 text-[11px] flex items-start gap-1.5" data-testid="confirm-too-far">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          For accuracy, please only drop spots near your current location.
        </div>
      )}
      {farWarn && (
        <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-[11px] flex items-start gap-1.5" data-testid="confirm-far-warn">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          This spot is far from your current location. Please confirm it is accurate.
        </div>
      )}

      <button
        type="button"
        onClick={onConfirm}
        disabled={publishing || tooFar}
        className="mt-3 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
        data-testid="publish-spot"
      >
        {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</> : <><Check className="w-4 h-4" /> Confirm and publish spot</>}
      </button>
    </motion.div>
  );
}

function _haversine_m_client(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function draftIcon() {
  return L.divIcon({
    html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
      <div style="position:relative;">
        <div style="position:absolute;inset:-12px;border-radius:50%;background:#ccff00;opacity:0.4;filter:blur(10px);"></div>
        <div style="position:relative;width:44px;height:44px;border-radius:50%;background:#ccff00;border:5px solid #fff;box-shadow:0 6px 18px rgba(0,0,0,0.5);"></div>
      </div>
      <div style="margin-top:6px;padding:3px 10px;border-radius:8px;background:#ccff00;color:#0a0a0c;font:700 11px 'Manrope',sans-serif;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.3);">Tap or drag</div>
    </div>`,
    className: "spotdrop-draft-icon",
    iconSize: [90, 80],
    iconAnchor: [45, 22],
  });
}

/* ---------- Spot popup content ---------- */
function SpotPopupContent({ spot, onReport, onOccupied, onRemove, onClaim, onUnclaim }) {
  const meta = TYPE_META[spot.parking_type] || TYPE_META.normal;
  const expiresMs = new Date(spot.expires_at).getTime();
  const claimMs = spot.claim_expires_at ? new Date(spot.claim_expires_at).getTime() : null;
  const [secsLeft, setSecsLeft] = React.useState(Math.max(0, Math.round((expiresMs - Date.now())/1000)));
  const [claimLeft, setClaimLeft] = React.useState(claimMs ? Math.max(0, Math.round((claimMs - Date.now())/1000)) : 0);
  React.useEffect(() => {
    const t = setInterval(() => {
      setSecsLeft(Math.max(0, Math.round((expiresMs - Date.now())/1000)));
      if (claimMs) setClaimLeft(Math.max(0, Math.round((claimMs - Date.now())/1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [expiresMs, claimMs]);
  const mm = Math.floor(secsLeft/60), ss = secsLeft % 60;
  const cmm = Math.floor(claimLeft/60), css = claimLeft % 60;

  return (
    <div className="min-w-[210px]" data-testid={`spot-popup-${spot.id}`}>
      <div className="flex items-center gap-2">
        <span style={{ color: meta.color }}>{meta.icon}</span>
        <span className="font-semibold text-[14px] text-white">{meta.label}</span>
      </div>
      <div className="mt-1.5 text-[12px] text-zinc-300 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" />
        {secsLeft > 0 ? `Leaves in ${mm}:${String(ss).padStart(2, "0")}` : "Expired"}
      </div>

      {spot.has_photo && (
        <img
          src={`${API}/spots/${spot.id}/photo`}
          alt="Spot"
          className="mt-2 w-full h-24 object-cover rounded-lg"
          data-testid={`spot-photo-${spot.id}`}
        />
      )}

      {!spot.mine && spot.source === "spotted" && (
        <div
          className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-[#22d3ee]/10 border-[#22d3ee]/30 text-[#67e8f9]"
          data-testid={`spot-spotted-${spot.id}`}
        >
          Spotted free spot — no handover
        </div>
      )}


      {!spot.mine && spot.source !== "spotted" && (
        <div
          className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
            spot.wait_mode === "waiting"
              ? "bg-[#ccff00]/10 border-[#ccff00]/25 text-[#ccff00]"
              : "bg-amber-500/10 border-amber-500/30 text-amber-200"
          }`}
          data-testid={`spot-waitmode-${spot.id}`}
        >
          {spot.wait_mode === "waiting" ? "Driver will wait" : "Driver won't wait"}
        </div>
      )}

      {spot.claimed && (
        <div className="mt-2 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-1.5 text-[11px] text-amber-200" data-testid={`spot-claimed-${spot.id}`}>
          <Users className="w-3 h-3" />
          {spot.claimed_by_me ? `You're heading here · ${cmm}:${String(css).padStart(2,"0")} left` : "A driver is heading here"}
        </div>
      )}

      {spot.possibly_occupied && !spot.mine && (
        <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-amber-500/12 border-amber-500/40 text-amber-200" data-testid={`spot-possibly-occupied-${spot.id}`}>
          <AlertTriangle className="w-3 h-3" /> Possibly occupied
        </div>
      )}

      {spot.report_count > 0 && (
        <div className="mt-1.5 text-[11px] text-amber-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {spot.report_count} report{spot.report_count > 1 ? "s" : ""}
        </div>
      )}

      <div className="mt-3 flex flex-col gap-2">
        {spot.mine ? (
          <button onClick={() => onRemove(spot)} className="h-10 rounded-lg bg-red-500/15 border border-red-500/30 text-red-200 text-[13px] font-semibold inline-flex items-center justify-center gap-1.5" data-testid={`spot-remove-${spot.id}`}>
            <Trash2 className="w-4 h-4" /> Remove
          </button>
        ) : spot.claimed_by_me ? (
          <button onClick={() => onUnclaim(spot)} className="h-10 rounded-lg bg-[#141416] border border-white/15 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-1.5" data-testid={`spot-unclaim-${spot.id}`}>
            Cancel heading there
          </button>
        ) : (
          <button
            onClick={() => onClaim(spot)}
            disabled={spot.claimed}
            className="h-11 rounded-lg bg-[#ccff00] text-[#0a0a0c] text-[13px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid={`spot-claim-${spot.id}`}
          >
            <Navigation className="w-4 h-4" /> {spot.claimed ? "Already claimed" : "I'm going there"}
          </button>
        )}
        {!spot.mine && (
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => onOccupied(spot)} className="h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-[12px] font-semibold inline-flex items-center justify-center gap-1.5" data-testid={`spot-occupied-${spot.id}`}>
              <Ban className="w-3.5 h-3.5" /> Already occupied
            </button>
            <button onClick={() => onReport(spot)} className="h-9 rounded-lg bg-[#141416] border border-white/10 text-amber-200 text-[12px] font-medium inline-flex items-center justify-center gap-1.5" data-testid={`spot-report-${spot.id}`}>
              <Flag className="w-3.5 h-3.5" /> Report fake
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* Memoised marker: only re-renders when this spot's own state changes, so a
   poll that returns the same data costs nothing. */
const SpotMarker = React.memo(
  function SpotMarker({ spot, handlers, markerRefs }) {
    return (
      <Marker
        position={[spot.lat, spot.lng]}
        icon={buildPin(spot)}
        zIndexOffset={1000}
        ref={(m) => {
          if (m) markerRefs.current[spot.id] = m;
        }}
      >
        <Popup>
          <SpotPopupContent
            spot={spot}
            onReport={handlers.onReport}
            onOccupied={handlers.onOccupied}
            onRemove={handlers.onRemove}
            onClaim={handlers.onClaim}
            onUnclaim={handlers.onUnclaim}
          />
        </Popup>
      </Marker>
    );
  },
  (a, b) =>
    a.handlers === b.handlers &&
    spotsSignature([a.spot]) === spotsSignature([b.spot]) &&
    a.spot.lat === b.spot.lat &&
    a.spot.lng === b.spot.lng
);

/* ---------- Main app page ---------- */
export default function AppPage() {
  const deviceId = React.useMemo(() => getDeviceId(), []);
  const geo = useGeolocation();
  const [spots, setSpots] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);
  const [polling, setPolling] = React.useState(false);
  const [draft, setDraft] = React.useState(null);     // {parking_type, duration_min}
  const [pinPos, setPinPos] = React.useState(null);   // {lat, lng}
  const [publishing, setPublishing] = React.useState(false);
  const [conflictPrompt, setConflictPrompt] = React.useState(false);
  // Driver identity modal:
  //  driverModal = null | { mode: "required" | "edit", after: () => void }
  const [driverModal, setDriverModal] = React.useState(null);
  const [carLocationOpen, setCarLocationOpen] = React.useState(false);
  const [groupsOpen, setGroupsOpen] = React.useState(false);
  // Private parking (map category + listings)
  // Map 2.0 layers (replaces the old All | Live | Private chips; live spots and
  // private listings are ON by default so existing behaviour is preserved).
  const [layers, setLayers] = React.useState(loadLayers);
  const updateLayers = React.useCallback((next) => {
    setLayers(next);
    saveLayers(next);
  }, []);
  const placeKinds = React.useMemo(
    () => PLACE_LAYER_KINDS.filter((k) => layers[k]),
    [layers]
  );
  const [listings, setListings] = React.useState([]);
  const [nearbyOpen, setNearbyOpen] = React.useState(false);
  const maybeAskToSaveParkingRef = React.useRef(null);
  const leftAppRef = React.useRef(false);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [parkLeft, setParkLeft] = React.useState(() => {
    const t = readTimer();
    return t ? secondsLeft(t) : null;
  });
  // Discreet countdown on the My Car button; hiding the panel never stops it.
  React.useEffect(() => {
    const refresh = () => {
      const t = readTimer();
      setParkLeft(t ? secondsLeft(t) : null);
    };
    syncTimer().then(refresh);
    const id = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    window.addEventListener("spotdrop-timer", refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("spotdrop-timer", refresh);
    };
  }, []);
  const mapInstanceRef = React.useRef(null);
  // Stable startup viewport: user location → last valid viewport → Malta.
  const initialView = React.useMemo(() => {
    if (geo.pos) return { center: [geo.pos.lat, geo.pos.lng], zoom: 16 };
    const saved = readSavedView();
    if (saved) return { center: [saved.lat, saved.lng], zoom: saved.zoom };
    return { center: MALTA_CENTER, zoom: 13 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Full-screen map mode: stops the mobile browser chrome from resizing the
  // viewport mid-gesture (which made the floating controls drift up and down).
  React.useEffect(() => {
    document.body.classList.add("app-map-mode");
    return () => document.body.classList.remove("app-map-mode");
  }, []);
  const [activeListing, setActiveListing] = React.useState(null);
  const [ppOpen, setPpOpen] = React.useState(false);
  const [quickOpen, setQuickOpen] = React.useState(false);
  const [quickBusy, setQuickBusy] = React.useState(false);
  // Activation funnel signals (once per session).
  React.useEffect(() => {
    trackActivation("app_open");
    track("map_opened");
  }, []);
  React.useEffect(() => {
    if (geo.pos) trackActivation("location_granted");
  }, [geo.pos]);
  const refreshListings = React.useCallback(async () => {
    try {
      setListings(await fetchListings(geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng } : null));
    } catch {
      /* keep previous listings */
    }
  }, [geo.pos]);
  React.useEffect(() => {
    if (!layers.private) return;
    refreshListings();
  }, [layers.private, refreshListings]);
  // Friends & Family map overlay: other members' live dots + shared parked cars.
  const [groupOverlay, setGroupOverlay] = React.useState({ members: [], cars: [], my_sharing_live: false });
  // CARTO basemap key. `null` = still resolving; "" = no key available, in which
  // case we fall back to plain OpenStreetMap tiles so users never see CARTO's
  // "API KEY REQUIRED" watermark.
  const [cartoKey, setCartoKey] = React.useState(
    () => process.env.REACT_APP_CARTO_API_KEY || localStorage.getItem("spotdrop_carto_key") || null
  );
  React.useEffect(() => {
    if (process.env.REACT_APP_CARTO_API_KEY) return;
    let alive = true;
    api
      .get("/map-config")
      .then((r) => {
        if (!alive) return;
        const key = r.data?.carto_key || "";
        setCartoKey((prev) => (prev === key ? prev : key));
        if (key) localStorage.setItem("spotdrop_carto_key", key);
        else localStorage.removeItem("spotdrop_carto_key");
      })
      .catch(() => alive && setCartoKey((prev) => (prev === null ? "" : prev)));
    return () => {
      alive = false;
    };
  }, []);

  // Deep-link from the landing: /app?open=save-car opens the Save-my-car sheet
  // on mount (only once — the URL param is stripped after use).
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("open") === "save-car") {
        setCarLocationOpen(true);
        url.searchParams.delete("open");
        const search = url.searchParams.toString();
        window.history.replaceState({}, "", url.pathname + (search ? `?${search}` : "") + url.hash);
      }
    } catch {
      /* ignore */
    }
  }, []);
  // Soft inline hint shown after a user dismisses the REQUIRED profile modal.
  // Auto-hides on any map interaction or when the profile becomes complete.
  const [showProfileHint, setShowProfileHint] = React.useState(false);
  const [mapStyle, setMapStyle] = React.useState(() => {
    if (typeof window === "undefined") return "standard";
    return localStorage.getItem("spotdrop_map_style") === "satellite" ? "satellite" : "standard";
  });
  React.useEffect(() => {
    try { localStorage.setItem("spotdrop_map_style", mapStyle); } catch { /* ignore */ }
  }, [mapStyle]);

  const basemap = React.useMemo(() => {
    if (mapStyle === "satellite") {
      return {
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP",
      };
    }
    if (cartoKey) {
      return { url: cartoUrlFor(cartoKey), attribution: "&copy; OpenStreetMap &copy; CARTO" };
    }
    return {
      url: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      overlay: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
      attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, HERE",
    };
  }, [mapStyle, cartoKey]);
  const markerRefs = React.useRef({});

  /** Runs `fn` only if the user's driver profile is complete. Otherwise
   *  opens the mandatory identity modal and runs `fn` after they save. */
  const requireProfile = React.useCallback((fn) => {
    if (isProfileComplete()) { fn(); return; }
    setShowProfileHint(false);
    setDriverModal({ mode: "required", after: fn });
  }, []);

  React.useEffect(() => { geo.start(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const fetchSpots = React.useCallback(async () => {
    const lat = geo.pos?.lat ?? MALTA_CENTER[0];
    const lng = geo.pos?.lng ?? MALTA_CENTER[1];
    try {
      setPolling(true);
      const { data } = await api.get("/spots/nearby", { params: { lat, lng, radius_m: NEARBY_RADIUS_M } });
      // Only re-render when something actually changed, so a 20 s poll with no
      // new activity costs nothing on the map.
      setSpots((prev) => (spotsSignature(prev) === spotsSignature(data) ? prev : data));
    } catch (err) {
      // Surface the failure once so users (and we) don't silently lose updates.
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? "Could not refresh spots (bad request)." : "Could not refresh spots.";
      // eslint-disable-next-line no-console
      console.warn("fetchSpots failed:", err?.response?.status, detail);
      toast.error(msg);
    } finally {
      setPolling(false);
    }
  }, [geo.pos]);

  React.useEffect(() => {
    fetchSpots();
    const t = setInterval(fetchSpots, POLL_MS);
    return () => clearInterval(t);
  }, [fetchSpots]);

  // Guards a post against double taps, and makes sure a created spot becomes
  // visible even if the first refresh raced the write or briefly failed.
  const postingRef = React.useRef(false);
  const refreshSpotsWithRetry = React.useCallback(() => {
    fetchSpots();
    setTimeout(fetchSpots, 1500);
    setTimeout(fetchSpots, 5000);
  }, [fetchSpots]);


  // Groups overlay poll (30 s). Also pushes my own position while sharing is
  // ON — foreground only, no background tracking.
  const refreshGroups = React.useCallback(async () => {
    const data = await fetchGroupOverlay();
    if (!data) return;
    setGroupOverlay(data);
    if (data.my_sharing_live && geo.pos) sendLiveBeat(geo.pos.lat, geo.pos.lng);
  }, [geo.pos]);

  React.useEffect(() => {
    refreshGroups();
    const t = setInterval(refreshGroups, 30000);
    return () => clearInterval(t);
  }, [refreshGroups]);

  const handleReport = React.useCallback(async (spot) => {
    try {
      const { data } = await api.post(`/spots/${spot.id}/report`);
      if (data.already_reported) toast.message("You already reported this spot.");
      else if (data.hidden) toast.success("Reported. Spot hidden after multiple reports.");
      else toast.success("Report submitted.");
      fetchSpots();
    } catch (err) {
      toast.error(errorMessage(err, "Could not report. Please try again."));
    }
  }, [fetchSpots]);

  const handleOccupied = React.useCallback(async (spot) => {
    try {
      const { data } = await api.post(`/spots/${spot.id}/occupied`);
      if (data.already_reported) toast.message("You already reported this as occupied.");
      else if (data.hidden) toast.success("Thanks — spot removed after multiple reports.");
      else toast.success("Thanks — marked as possibly occupied.");
      fetchSpots();
    } catch (err) {
      toast.error(errorMessage(err, "Could not report as occupied. Please try again."));
    }
  }, [fetchSpots]);

  const handleRemove = React.useCallback(async (spot) => {
    try {
      await api.delete(`/spots/${spot.id}`);
      toast.success("Spot removed.");
      setSpots((s) => s.filter((x) => x.id !== spot.id));
    } catch (err) {
      toast.error(errorMessage(err, "Could not remove this spot. Please try again."));
    }
  }, []);

  const handleClaim = React.useCallback((spot) => {
    requireProfile(async () => {
      try {
        await api.post(`/spots/${spot.id}/claim`, { claimer_profile: getProfile() });
        toast.success("On your way. Other drivers can see you're heading there.");
        markEngaged();
        maybeAskToSaveParkingRef.current?.();
        fetchSpots();
      } catch (err) {
        toast.error(errorMessage(err, "Could not mark as heading there. Please try again."));
      }
    });
  }, [requireProfile, fetchSpots]);

  const handleUnclaim = React.useCallback(async (spot) => {
    try {
      await api.delete(`/spots/${spot.id}/claim`);
      toast.message("Cancelled.");
      fetchSpots();
    } catch (err) {
      toast.error(errorMessage(err, "Could not cancel. Please try again."));
    }
  }, [fetchSpots]);

  const spotHandlers = React.useMemo(
    () => ({
      onReport: handleReport,
      onOccupied: handleOccupied,
      onRemove: handleRemove,
      onClaim: handleClaim,
      onUnclaim: handleUnclaim,
    }),
    [handleReport, handleOccupied, handleRemove, handleClaim, handleUnclaim]
  );

  // Surfaces the spot the user has currently claimed (if any).
  const myClaim = spots.find((s) => s.claimed_by_me) || null;
  const mySpot = React.useMemo(() => spots.find((s) => s.mine) || null, [spots]);

  // Stable props for the Nearby sheet: a phone's watchPosition fires constantly,
  // so the position is rounded (~11 m) and the arrays/callbacks are memoised.
  const posKey = geo.pos ? `${geo.pos.lat.toFixed(4)},${geo.pos.lng.toFixed(4)}` : "";
  const nearbyPos = React.useMemo(() => {
    if (!posKey) return null;
    const [lat, lng] = posKey.split(",").map(Number);
    return { lat, lng };
  }, [posKey]);
  const liveSpotsForNearby = React.useMemo(() => (layers.live ? spots : EMPTY_SPOTS), [layers.live, spots]);
  const closeNearby = React.useCallback(() => {
    setNearbyOpen(false);
    maybeAskToSaveParkingRef.current?.();
  }, []);  const focusSpot = React.useCallback((s) => {
    const map = mapInstanceRef.current;
    if (map) map.flyTo([s.lat, s.lng], 17, { duration: 0.7 });
    setTimeout(() => markerRefs.current[s.id]?.openPopup(), 850);
  }, []);

  /* ---- Parking Journey (destination → options → success / next option) ---- */
  const [destOpen, setDestOpen] = React.useState(false);
  const [destBias, setDestBias] = React.useState(null);
  const [journey, setJourney] = React.useState(() => {
    const j = loadJourney();
    // Came back from Google Maps / Waze (or reloaded) mid-navigation. The
    // question is only asked once enough time has passed to make it plausible.
    return j && j.status === "navigating" && askReady(j) ? saveJourney({ ...j, status: "asking" }) : j;
  });

  const updateJourney = React.useCallback((next) => {
    if (!next) {
      clearJourney();
      setJourney(null);
      return;
    }
    setJourney(saveJourney(next));
  }, []);

  const focusOption = React.useCallback((o) => {
    const map = mapInstanceRef.current;
    if (!map || !o) return;
    // Car parks and street sections are areas: fit what the driver is heading to,
    // capped so the map never slams to maximum zoom.
    const coords =
      o.geometry?.type === "LineString"
        ? o.geometry.coordinates
        : o.geometry?.type === "Polygon"
        ? o.geometry.coordinates[0]
        : null;
    if (coords?.length > 1) {
      map.fitBounds(
        L.latLngBounds(coords.map(([lng, lat]) => [lat, lng])).pad(0.25),
        { maxZoom: 17, animate: true }
      );
      return;
    }
    map.flyTo([o.lat, o.lng], Math.max(map.getZoom(), 17), { duration: 0.8 });
  }, []);

  // Returning to SpotDrop with a journey in flight → ask once, never reset the
  // map. The question needs a real app switch AND enough elapsed time; it is a
  // question, never a confirmation, and "I parked" stays available meanwhile.
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!leftAppRef.current) return;
      leftAppRef.current = false;
      setJourney((j) =>
        j && j.status === "navigating" && askReady(j) ? saveJourney({ ...j, status: "asking" }) : j
      );
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") leftAppRef.current = true;
    };
    document.addEventListener("visibilitychange", onHidden);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  const startJourney = React.useCallback(
    (destination, selected, backups) => {
      updateJourney({ destination, selected, backups, started_at: Date.now(), status: "selected" });
      setDestOpen(false);
      setTimeout(() => focusOption(selected), 140);
    },
    [updateJourney, focusOption]
  );

  const journeyDirections = React.useCallback(
    (app) => {
      const o = journey?.selected;
      if (!o) return;
      const chosen = app === "waze" ? "waze" : "google";
      const target = navTarget(o, geo.pos);
      track("directions_opened", { kind: o.kind, app: chosen, target: target.via });
      updateJourney({ ...journey, status: "navigating", directions_at: Date.now() });
      window.open(buildNavAppUrl(chosen, target.lat, target.lng), "_blank", "noopener");
    },
    [journey, updateJourney, geo.pos]
  );

  const showNextOption = React.useCallback(
    (fromFailure) => {
      const backups = journey?.backups || [];
      const next = backups[0];
      if (!next) {
        toast.message("That's the last option we know near there.");
        updateJourney({ ...journey, status: "asking" });
        return;
      }
      track("next_option_selected", { kind: next.kind, after_failure: !!fromFailure });
      updateJourney({ ...journey, status: "next", pending: next, backups: backups.slice(1) });
      focusOption(next);
    },
    [journey, updateJourney, focusOption]
  );

  const journeyFound = React.useCallback(() => {
    if (!journey) return;
    track("parking_found", { kind: journey.selected.kind });
    track("parking_journey_completed", { result: "parked" });
    markEngaged();
    updateJourney({ ...journey, status: "parked" });
  }, [journey, updateJourney]);

  const journeyFailed = React.useCallback(() => {
    if (!journey) return;
    const o = journey.selected;
    track("parking_failed", { kind: o.kind });
    if (o.source === "place" && o.place_id) penalizePlace(o.place_id);
    showNextOption(true);
  }, [journey, showNextOption]);

  const journeyTryAnother = React.useCallback(() => {
    if (!journey) return;
    track("try_another_selected");
    showNextOption(false);
  }, [journey, showNextOption]);

  const journeyGoThere = React.useCallback(() => {
    const next = journey?.pending;
    if (!next) return;
    updateJourney({ ...journey, selected: next, pending: null, status: "selected", directions_at: null });
    focusOption(next);
  }, [journey, updateJourney, focusOption]);

  const endJourney = React.useCallback(() => {
    if (journey && journey.status !== "parked") track("parking_journey_completed", { result: "ended" });
    updateJourney(null);
  }, [journey, updateJourney]);

  const journeyWalkToDestination = React.useCallback(() => {
    const d = journey?.destination;
    if (!d) return;
    const url = buildNavigationUrl(d.lat, d.lng);
    if (url.startsWith("maps://")) window.location.href = url;
    else window.open(url, "_blank", "noopener");
  }, [journey]);

  const journeyHighlight = journey ? (journey.status === "next" ? journey.pending : journey.selected) : null;

  // While a destination is chosen, only its own options stay bright on the map.
  // Journey option ids are prefixed (`place-<id>`), so use the raw place id.

  // The chosen destination's own options are always drawn, even when their kind
  // layer is switched off — otherwise the recommendation has no marker.
  const journeyFocusPlaces = React.useMemo(() => {
    if (!journey) return [];
    return [journey.selected, journey.pending, ...(journey.backups || [])]
      .filter((o) => o?.place_id)
      .map((o) => ({
        id: o.place_id,
        kind: o.kind,
        lat: o.lat,
        lng: o.lng,
        name: o.name || null,
        geometry: o.geometry || null,
        selected: o.place_id === journeyHighlight?.place_id,
      }));
  }, [journey, journeyHighlight]);

  /* ---- Phase 2: onboarding, coach marks, saved-parking discoverability ---- */
  const [introOpen, setIntroOpen] = React.useState(() => !onboardingSeen());
  const [introReplay, setIntroReplay] = React.useState(false);
  const [tipsOpen, setTipsOpen] = React.useState(false);
  const [tipsReplay, setTipsReplay] = React.useState(false);
  const [carRecord, setCarRecord] = React.useState(() => getCarLocation());
  const [parkPrompt, setParkPrompt] = React.useState(false);

  const refreshCarRecord = React.useCallback(() => setCarRecord(getCarLocation()), []);
  React.useEffect(() => {
    if (!carLocationOpen) refreshCarRecord();
  }, [carLocationOpen, refreshCarRecord]);

  const finishIntro = React.useCallback((skipped) => {
    setIntroOpen(false);
    if (!introReplay) {
      markOnboardingSeen();
      if (!coachMarksSeen()) setTimeout(() => setTipsOpen(true), 450);
    }
    setIntroReplay(false);
    void skipped;
  }, [introReplay]);

  const finishTips = React.useCallback(() => {
    setTipsOpen(false);
    if (!tipsReplay) markCoachMarksSeen();
    setTipsReplay(false);
  }, [tipsReplay]);

  // Shown only after a real parking interaction (claim / nearby / drop), never
  // while onboarding or tips are up, and never if parking is already saved.
  const uiBlockedRef = React.useRef(false);
  React.useEffect(() => {
    uiBlockedRef.current = introOpen || tipsOpen;
  }, [introOpen, tipsOpen]);
  const maybeAskToSaveParking = React.useCallback(() => {
    setTimeout(() => {
      if (uiBlockedRef.current || getCarLocation() || !canShowParkingPrompt()) return;
      markParkingPromptShown();
      track("parking_memory_prompt_shown");
      setParkPrompt(true);
    }, 2500);
  }, []);
  React.useEffect(() => {
    maybeAskToSaveParkingRef.current = maybeAskToSaveParking;
  }, [maybeAskToSaveParking]);

  const openSavedParking = React.useCallback(() => {
    track("save_parking_clicked", { from: "header" });
    setCarLocationOpen(true);
  }, []);
  // The active handover: either a spot I own that has been claimed, or a spot I have claimed.
  const activeHandover = React.useMemo(() => {
    const claimedMine = spots.find((s) => s.mine && s.claimed && (s.handover_state || "pending") === "pending");
    if (claimedMine) return claimedMine;
    if (myClaim && (myClaim.handover_state || "pending") === "pending") return myClaim;
    return null;
  }, [spots, myClaim]);

  /** Entry point for the "Drop spot" button. If the user is already heading
   *  to another spot, ask them to release that claim before dropping a new spot.
   *  Also gates on the mandatory driver-identity profile. */
  const onTapDropSpot = () => {
    requireProfile(() => {
      if (myClaim) {
        setConflictPrompt(true);
        return;
      }
      setShowAdd(true);
    });
  };

  const releaseClaimAndDrop = async () => {
    if (!myClaim) {
      setConflictPrompt(false);
      setShowAdd(true);
      return;
    }
    try {
      await api.delete(`/spots/${myClaim.id}/claim`);
      toast.message("Cancelled your previous heading-there.");
      // optimistically clear it so the UI doesn't flicker
      setSpots((s) => s.map((x) => x.id === myClaim.id
        ? { ...x, claimed_by_me: false, claimed: false, claim_expires_at: null }
        : x
      ));
    } catch (err) {
      // Even if release fails (e.g. claim already expired), let the user proceed.
      // eslint-disable-next-line no-console
      console.warn("release claim failed:", err?.response?.data?.detail);
    } finally {
      setConflictPrompt(false);
      setShowAdd(true);
    }
  };

  // --- Stage 1 → Stage 2 transition ---
  const handleContinueToConfirm = (d) => {
    if (!geo.pos) { toast.error("Waiting for your location…"); return; }
    setDraft(d);
    setPinPos({ lat: geo.pos.lat, lng: geo.pos.lng });
    setShowAdd(false);
  };

  const cancelConfirm = () => {
    setDraft(null);
    setPinPos(null);
  };

  const publishDraft = async () => {
    if (!draft || !pinPos) return;
    setPublishing(true);
    try {
      await api.post("/spots", {
        lat: pinPos.lat,
        lng: pinPos.lng,
        parking_type: draft.parking_type,
        duration_min: draft.duration_min,
        wait_mode: draft.wait_mode || "waiting",
        leaver_profile: getProfile(),
        // Anti-abuse: sent to the backend's drop-distance guard.
        user_lat: geo.pos?.lat,
        user_lng: geo.pos?.lng,
        user_acc: geo.pos?.acc,
      });
      track("spot_posted", { source: "drop", parking_type: draft.parking_type, duration_min: draft.duration_min });
      toast.success("Spot published. Thank you.");
      markEngaged();
      maybeAskToSaveParkingRef.current?.();
      setDraft(null);
      setPinPos(null);
      refreshSpotsWithRetry();
    } catch (err) {
      track("spot_post_failed", { source: "drop", reason: errorReason(err) });
      toast.error(errorMessage(err, "Couldn't publish this spot. Please try again."));
    } finally {
      setPublishing(false);
    }
  };

  const publishSpotted = async ({ parking_type, duration_min, photo }) => {
    if (!geo.pos || quickBusy || postingRef.current) return;
    postingRef.current = true;
    setQuickBusy(true);
    try {
      const res = await api.post("/spots", {
        lat: geo.pos.lat,
        lng: geo.pos.lng,
        parking_type,
        duration_min,
        source: "spotted",
        user_lat: geo.pos.lat,
        user_lng: geo.pos.lng,
        user_acc: geo.pos.acc,
      });
      if (photo && res.data?.id) {
        // Optional and best-effort — a failed photo never fails the spot.
        const fd = new FormData();
        fd.append("photo", await resizePhoto(photo));
        api.post(`/spots/${res.data.id}/photo`, fd).then(fetchSpots).catch(() => {});
      }
      track("spot_posted", { source: "spotted", parking_type, duration_min });
      toast.success("Thanks! Spot posted for other drivers.");
      markEngaged();
      setQuickOpen(false);
      // The spot exists: if the refresh fails we retry, never re-post.
      refreshSpotsWithRetry();
    } catch (err) {
      track("spot_post_failed", { source: "spotted", reason: errorReason(err) });
      toast.error(errorMessage(err, "Couldn't post this spot. Please try again."));
    } finally {
      postingRef.current = false;
      setQuickBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a0c] text-white overflow-hidden" data-testid="app-root">
      {/* Loading veil — visible until we have a usable fix or 5s have passed */}
      {!geo.pos && !geo.error && (
        <div className="absolute inset-0 z-[3000] flex flex-col items-center justify-center bg-gradient-to-b from-[#0a0a0c] to-[#141416]" data-testid="app-loading">
          <div className="w-14 h-14 rounded-2xl bg-[#ccff00] flex items-center justify-center mb-5">
            <MapPin className="w-7 h-7 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-xl font-semibold text-white">Finding your location…</div>
          <div className="text-sm text-zinc-400 mt-2 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Please allow location when prompted
          </div>
          <Link to="/" className="mt-8 text-xs text-zinc-500 hover:text-white">← Back to home</Link>
        </div>
      )}

      {/* Map */}
      <MapContainer
        center={initialView.center}
        zoom={initialView.zoom}
        zoomControl={false}
        preferCanvas={true}
        zoomSnap={0.5}
        minZoom={10}
        maxZoom={19}
        maxBounds={MALTA_MAX_BOUNDS}
        maxBoundsViscosity={0.85}
        wheelPxPerZoomLevel={90}
        style={{ position: "absolute", inset: 0, background: "#0a0a0c" }}
      >
        {(cartoKey !== null || mapStyle === "satellite") && (
          <TileLayer
            key={basemap.url}
            attribution={basemap.attribution}
            url={basemap.url}
            maxZoom={19}
          />
        )}
        {cartoKey !== null && basemap.overlay && (
          <TileLayer key={basemap.overlay} url={basemap.overlay} maxZoom={19} />
        )}
        <ViewPersist />
        <ZoomTo pos={geo.pos} />
        {geo.pos && (
          <>
            <Circle center={[geo.pos.lat, geo.pos.lng]} radius={Math.min(geo.pos.acc || 50, 250)}
              pathOptions={{ color: (geo.pos.acc && geo.pos.acc > WARN_ACCURACY_M) ? "#f59e0b" : "#3b82f6", weight: 1, fillOpacity: 0.1 }} />
            <Marker position={[geo.pos.lat, geo.pos.lng]} icon={userIcon(geo.pos.acc && geo.pos.acc > WARN_ACCURACY_M)} />
          </>
        )}
        {layers.live && spots.map((s) => (
          <SpotMarker key={s.id} spot={s} handlers={spotHandlers} markerRefs={markerRefs} />
        ))}
        {(placeKinds.length > 0 || journeyFocusPlaces.length > 0 || layers.accessible || layers.community_pending) && (
          <ParkingPlacesLayer
            kinds={placeKinds}
            dim={placeKinds.length >= 3}
            focusPlaces={journeyFocusPlaces}
            includeAccessible={!!layers.accessible}
            includePendingCommunity={!!layers.community_pending}
          />
        )}
        {layers.private && listings.map((l) => (
          <Marker
            key={`pp-${l.id}`}
            position={[l.lat, l.lng]}
            icon={privateIcon(l)}
            eventHandlers={{ click: () => setActiveListing(l) }}
          />
        ))}
        {pinPos && (
          <Marker
            position={[pinPos.lat, pinPos.lng]}
            icon={draftIcon()}
            draggable={true}
            autoPan={true}
            autoPanPadding={[40, 220]}
            eventHandlers={{
              dragend: (e) => {
                const ll = e.target.getLatLng();
                setPinPos({ lat: ll.lat, lng: ll.lng });
              },
            }}
            data-testid="draft-pin"
          />
        )}
        {groupOverlay.members.map((m, i) => (
          <Marker key={`friend-${i}`} position={[m.lat, m.lng]} icon={friendIcon(m.nickname)}>
            <Popup>
              <div className="text-xs" data-testid={`friend-popup-${i}`}>
                <div className="font-semibold">{m.nickname}</div>
                <div className="opacity-70 mt-0.5">{m.group_name} · sharing live location</div>
                <div className="opacity-50 mt-0.5">Updated {formatRelativeTime(new Date(m.updated_at).getTime())}</div>
              </div>
            </Popup>
          </Marker>
        ))}
        {groupOverlay.cars.map((c, i) => (
          <Marker key={`friend-car-${i}`} position={[c.lat, c.lng]} icon={friendCarIcon(c.nickname)}>
            <Popup>
              <div className="text-xs" data-testid={`friend-car-popup-${i}`}>
                <div className="font-semibold">{c.nickname}&apos;s parked car</div>
                <div className="opacity-70 mt-0.5">{c.group_name}</div>
                {c.note && <div className="opacity-70 mt-0.5">{c.note}</div>}
                {c.saved_at && (
                  <div className="opacity-50 mt-0.5">Saved {formatRelativeTime(c.saved_at)}</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        {journey && (
          <>
            <Marker
              position={[journey.destination.lat, journey.destination.lng]}
              icon={destinationIcon(journey.destination.name)}
            />
            {journeyHighlight && (
              <Circle
                center={[journeyHighlight.lat, journeyHighlight.lng]}
                radius={35}
                pathOptions={{ color: "#ccff00", weight: 2, fillColor: "#ccff00", fillOpacity: 0.12 }}
              />
            )}
          </>
        )}
        <DraftTapMove enabled={!!draft} onMove={setPinPos} />
        <ClearOnMapInteract clearHint={() => setShowProfileHint(false)} clearCoach={() => {}} />
        {draft && <PanToDraft pinPos={pinPos} />}
        {!shareOpen && (
          <>
            <RecenterButton pos={geo.pos} onRefresh={geo.refresh} refreshing={geo.refreshing} />
            <MapStyleToggle style={mapStyle} onChange={setMapStyle} />
          </>
        )}
        <MapRefBridge refObj={mapInstanceRef} />
        <MySpotButton mySpot={mySpot} markerRefs={markerRefs} />
      </MapContainer>

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-[1000] p-2 sm:p-3 flex items-center justify-between pointer-events-none">
        <Link to="/" className="pointer-events-auto inline-flex items-center gap-2 px-2.5 sm:px-3 h-9 sm:h-10 rounded-full bg-[#0a0a0c]/85 border border-white/10 backdrop-blur" data-testid="app-logo">
          <div className="w-6 h-6 rounded-md bg-[#ccff00] flex items-center justify-center">
            <MapPin className="w-3.5 h-3.5 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-sm font-semibold">SpotDrop<span className="text-[#ccff00]">.</span></span>
        </Link>

        <div className="pointer-events-auto flex items-center gap-2 relative">
          <button
            type="button"
            onClick={() => {
              shareReferral((kind, msg) => {
                if (kind === "success") toast.success(msg);
                else if (kind === "error") toast.error(msg);
              });
            }}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-9 sm:h-10 rounded-full bg-[#0a0a0c]/85 border border-white/10 backdrop-blur text-[11px] text-zinc-200 hover:border-[#ccff00]/50 hover:bg-[#ccff00]/[0.05] active:scale-95 transition-transform"
            data-testid="app-invite-btn"
            aria-label="Share SpotDrop with a friend"
            title="Share SpotDrop"
          >
            <Share2 className="w-3.5 h-3.5 text-[#ccff00]" />
            <span className="hidden sm:inline">Invite</span>
          </button>
          <button
            type="button"
            onClick={openSavedParking}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-9 sm:h-10 rounded-full bg-[#0a0a0c]/85 border border-[#ccff00]/30 backdrop-blur text-[11px] text-zinc-100 active:scale-95 transition-transform"
            data-testid="save-parking-btn"
            aria-label="My Car"
          >
            <Car className="w-3.5 h-3.5 text-[#ccff00]" />
            <span className="whitespace-nowrap">My Car</span>
            {parkLeft !== null && (
              <span
                className={`whitespace-nowrap font-semibold ${parkLeft <= 0 ? "text-red-400" : "text-[#ccff00]"}`}
                data-testid="save-parking-timer"
              >
                · {parkLeft <= 0 ? "time up" : formatLeftShort(parkLeft)}
              </span>
            )}
          </button>
          <div className="flex items-center gap-1.5 px-3 h-10 rounded-full bg-[#0a0a0c]/85 border border-white/10 backdrop-blur text-[11px] sr-only">
            {polling ? <Loader2 className="w-3 h-3 animate-spin text-[#ccff00]" /> : <span className="w-2 h-2 rounded-full bg-[#ccff00]" />}
            <span className="text-zinc-300 whitespace-nowrap" data-testid="spot-count">Live spots</span>
          </div>

        </div>
      </div>

      {/* Clean loading state while the basemap config resolves — never a technical error. */}
      {cartoKey === null && mapStyle !== "satellite" && (
        <div
          className="absolute inset-0 z-[900] flex flex-col items-center justify-center bg-[#0a0a0c] pointer-events-none"
          data-testid="map-preparing"
        >
          <Loader2 className="w-5 h-5 animate-spin text-[#ccff00]" />
          <div className="mt-3 text-[13px] text-zinc-400">Preparing map…</div>
        </div>
      )}

      <MapLayersControl
        layers={layers}
        onChange={updateLayers}
        onOpenNearby={() => setNearbyOpen(true)}
      />

      {/* Compact "More" sheet — secondary actions moved off the map */}
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[2000] bg-black/60"
              onClick={() => setMoreOpen(false)}
              data-testid="more-menu-backdrop"
            />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 280 }}
              className="fixed inset-x-0 bottom-0 z-[2001] bg-[#141416] border-t border-white/10 rounded-t-3xl p-4 pb-7"
              data-testid="more-menu-sheet"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="font-heading text-lg font-semibold text-white">More</div>
                <button
                  type="button"
                  onClick={() => setMoreOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                  data-testid="more-menu-close"
                  aria-label="Close more options"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
              <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setMoreOpen(false); setGroupsOpen(true); }}
                  className="w-full px-3 py-3 flex items-center gap-3 text-left active:bg-white/5"
                  data-testid="app-groups-btn"
                  aria-label="Open Friends and Family groups"
                >
                  <Users className="w-4 h-4 text-[#22d3ee] shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] text-white">Friends &amp; Family</span>
                    <span className="block text-[11px] text-zinc-500">Share your location with your group</span>
                  </span>
                  {groupOverlay.my_sharing_live && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#22d3ee] animate-pulse" />
                  )}
                  <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => { setMoreOpen(false); setPpOpen(true); }}
                  className="w-full px-3 py-3 flex items-center gap-3 text-left active:bg-white/5"
                  data-testid="private-parking-btn"
                >
                  <Warehouse className="w-4 h-4 text-[#ccff00] shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] text-white">List / manage my space</span>
                    <span className="block text-[11px] text-zinc-500">Rent out your private parking</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    shareReferral(
                      (kind, msg) => {
                        if (kind === "success") toast.success(msg);
                        else if (kind === "error") toast.error(msg);
                      },
                      { title: "SpotDrop Malta", text: INVITE_FF_MESSAGE, copyMessage: "Link copied!" }
                    );
                  }}
                  className="w-full px-3 py-3 flex items-center gap-3 text-left active:bg-white/5"
                  data-testid="invite-ff-btn"
                >
                  <UserPlus className="w-4 h-4 text-[#ccff00] shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] text-white">Invite Friends &amp; Family</span>
                    <span className="block text-[11px] text-zinc-500">Share your invite link</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <DestinationPill
        journey={journey}        onOpen={() => {
          setDestBias(geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng } : null);
          setDestOpen(true);
        }}
        onClear={endJourney}
      />

      <DestinationSheet
        open={destOpen}
        onClose={() => setDestOpen(false)}
        bias={destBias}
        liveSpots={liveSpotsForNearby}
        journey={journey}
        onStart={startJourney}
      />

      <AnimatePresence>
        {journey && !destOpen && !draft && (
          <JourneyCard
            journey={journey}
            onDirections={journeyDirections}
            onFound={journeyFound}
            onFailed={journeyFailed}
            onTryAnother={journeyTryAnother}
            onGoThere={journeyGoThere}
            onSaveParking={() => {
              track("save_parking_clicked", { from: "journey" });
              setCarLocationOpen(true);
            }}
            onWalkToDestination={journeyWalkToDestination}
            onDone={endJourney}
          />
        )}
      </AnimatePresence>

      {/* Legend — colour key only, no counters */}
      {/* Live-spot type legend lives inside the Layers sheet now, keeping the map clean. */}

      {/* Soft profile hint — shown only after the required identity modal was dismissed
          without saving. Auto-hides on any map interaction or once the profile is saved. */}
      <AnimatePresence>
        {showProfileHint && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            onClick={() => setShowProfileHint(false)}
            className="absolute left-3 right-3 top-28 z-[999] px-3 py-2 rounded-xl bg-[#0a0a0c]/85 border border-white/10 backdrop-blur text-[12px] text-zinc-200 flex items-center gap-2 pointer-events-auto cursor-pointer"
            data-testid="profile-hint"
            role="status"
          >
            <UserCog className="w-3.5 h-3.5 text-[#ccff00] shrink-0" />
            <span className="leading-snug">Drop or claim a spot to finish setting up your driver profile.</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Low accuracy banner */}
      {geo.pos && geo.pos.acc != null && geo.pos.acc > WARN_ACCURACY_M && (
        <div
          className="absolute left-3 right-3 top-28 z-[1000] p-3 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-100 text-xs flex items-start gap-2"
          data-testid="low-accuracy-banner"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Location accuracy is low (±{Math.round(geo.pos.acc)} m)</div>
            <div className="opacity-90 mt-0.5">
              Please wait a few seconds or move slightly for a better GPS signal. Tap the crosshair to refresh.
            </div>
          </div>
        </div>
      )}

      {/* Geo error */}
      {geo.error && !geo.granted && (
        <div className="absolute left-3 right-3 bottom-28 z-[1000] p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-start gap-2" data-testid="geo-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Location needed</div>
            <div className="opacity-80 mt-0.5">SpotDrop needs your location to drop and find nearby spots. Please enable location in your browser.</div>
          </div>
        </div>
      )}

      {/* Floating add button — hidden while confirming a draft */}
      {!draft && (
        <>
          {!journey && (
          <div
            className="absolute left-2 right-16 sm:left-3 sm:right-auto bottom-24 sm:bottom-28 z-[1000] flex flex-col items-start gap-1.5 sm:gap-2 pointer-events-none"
            data-testid="campaign-card-slot"
          >
            {parkPrompt && (
              <ParkingMemoryPrompt
                onSave={() => {
                  setParkPrompt(false);
                  markParkingPromptAccepted();
                  track("parking_memory_prompt_accepted");
                  track("save_parking_clicked", { from: "prompt" });
                  setCarLocationOpen(true);
                }}
                onDismiss={() => {
                  setParkPrompt(false);
                  markParkingPromptDismissed();
                  track("parking_memory_prompt_dismissed");
                }}
              />
            )}
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className="pointer-events-auto w-auto inline-flex items-center gap-1.5 px-3 h-9 sm:h-10 rounded-full bg-[#0a0a0c]/70 sm:bg-[#0a0a0c]/[0.92] border border-white/15 backdrop-blur-md sm:backdrop-blur text-[12px] text-zinc-100 shadow-md sm:shadow-none active:scale-95 transition-transform"
              data-testid="more-menu-btn"
              aria-label="More options"
            >
              <MoreHorizontal className="w-3.5 h-3.5 text-[#ccff00]" />
              More
              {groupOverlay.my_sharing_live && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#22d3ee] animate-pulse" data-testid="groups-sharing-dot" />
              )}
            </button>
            <CampaignCard />
          </div>
          )}
          {shareOpen && (
            <div
              className="fixed inset-0 z-[1550]"
              onClick={() => setShareOpen(false)}
              data-testid="share-actions-backdrop"
            />
          )}
          <div
            className="absolute right-3 z-[1600] flex flex-col items-end gap-2"
            style={{
              bottom: journey
                ? "calc(var(--journey-card-h, 194px) + env(safe-area-inset-bottom, 0px) + 1.25rem)"
                : "calc(2rem + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <AnimatePresence>
              {shareOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col items-end gap-2"
                  data-testid="share-actions-menu"
                >
                  <button
                    type="button"
                    onClick={() => { setShareOpen(false); onTapDropSpot(); }}
                    className="h-11 px-4 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(0,0,0,0.5)] active:scale-95 transition-transform"
                    data-testid="add-spot-btn"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} /> I'm leaving a spot
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShareOpen(false); setQuickOpen(true); }}
                    className="h-11 px-4 rounded-full bg-[#0a0a0c]/95 border border-[#22d3ee]/40 backdrop-blur text-[#e4e4e7] font-medium text-[13px] inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(0,0,0,0.5)] active:scale-95 transition-transform"
                    data-testid="saw-spot-btn"
                  >
                    <Eye className="w-4 h-4 text-[#22d3ee]" /> I saw a free spot
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShareOpen(false);
                      track("report_parking_clicked", { from: "share_menu" });
                      setReportOpen(true);
                    }}
                    className="h-11 px-4 rounded-full bg-[#0a0a0c]/95 border border-white/15 backdrop-blur text-[#e4e4e7] font-medium text-[13px] inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(0,0,0,0.5)] active:scale-95 transition-transform"
                    data-testid="report-parking-btn"
                  >
                    <Flag className="w-4 h-4 text-[#ccff00]" /> Report parking info
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            <button
              type="button"
              onClick={() => setShareOpen((v) => !v)}
              className="h-14 px-4 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(0,0,0,0.5)] active:scale-95 transition-transform"
              data-testid="share-spot-fab"
              aria-expanded={shareOpen}
              aria-label="Share a spot"
            >
              {shareOpen ? <X className="w-5 h-5" strokeWidth={2.5} /> : <Plus className="w-5 h-5" strokeWidth={2.5} />}
              Share a Spot
            </button>
          </div>
        </>
      )}

      <AddSpotSheet
        open={showAdd}
        onClose={() => setShowAdd(false)}
        pos={geo.pos}
        onContinue={handleContinueToConfirm}
      />

      <AnimatePresence>
        <ConfirmLocationBar
          open={!!draft}
          draft={draft}
          userPos={geo.pos}
          pinPos={pinPos}
          publishing={publishing}
          onConfirm={publishDraft}
          onCancel={cancelConfirm}
        />
      </AnimatePresence>

      {/* Conflict prompt: user tapped "Drop spot" while already heading to another spot */}
      <AnimatePresence>
        {conflictPrompt && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[2500] bg-black/70"
              onClick={() => setConflictPrompt(false)}
              data-testid="conflict-backdrop"
            />
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed inset-x-3 bottom-6 z-[2501] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-10 sm:max-w-[420px] sm:w-[92vw] bg-[#141416] border border-white/10 rounded-3xl p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="conflict-prompt-title"
              data-testid="conflict-prompt"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-300" />
                </div>
                <div className="flex-1">
                  <div id="conflict-prompt-title" className="font-heading text-lg font-semibold text-white leading-tight">
                    You&apos;re already heading to a spot
                  </div>
                  <p className="mt-1.5 text-[13px] text-zinc-400 leading-snug">
                    Cancel your current &ldquo;I&apos;m going there&rdquo; claim to drop a new spot, or keep heading to the one you already claimed.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={releaseClaimAndDrop}
                  className="h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  data-testid="conflict-cancel-and-drop"
                >
                  Cancel claim and drop spot
                </button>
                <button
                  type="button"
                  onClick={() => setConflictPrompt(false)}
                  className="h-12 rounded-full bg-[#0a0a0c] border border-white/15 text-white font-medium text-sm active:scale-[0.98] transition-transform"
                  data-testid="conflict-keep"
                >
                  Keep heading there
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {activeHandover && (
        <HandoverPanel
          spot={activeHandover}
          onSettled={() => fetchSpots()}
        />
      )}

      <DriverIdentityModal
        open={!!driverModal}
        mode={driverModal?.mode || "edit"}
        onSaved={() => {
          const after = driverModal?.after;
          setDriverModal(null);
          setShowProfileHint(false);
          if (typeof after === "function") setTimeout(after, 0);
          toast.success("Driver details saved.");
        }}
        onClose={() => {
          // If the user backed out of the *required* modal without saving, show
          // a soft hint at the top of the map so they don't feel lost.
          if (driverModal?.mode === "required" && !isProfileComplete()) {
            setShowProfileHint(true);
          }
          setDriverModal(null);
        }}
        onOpenCarLocation={() => {
          setDriverModal(null);
          track("save_my_car_clicked", { from: "driver_modal" });
          setTimeout(() => setCarLocationOpen(true), 60);
        }}
      />

      <CarLocationSheet
        open={carLocationOpen}
        onClose={() => setCarLocationOpen(false)}
        userPos={geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng, accuracy: geo.pos.acc } : null}
        onSaved={refreshCarRecord}
        onOpenDriverDetails={() => {
          setCarLocationOpen(false);
          setTimeout(() => setDriverModal({ mode: "edit" }), 80);
        }}
        onReplayIntro={() => {
          setCarLocationOpen(false);
          setIntroReplay(true);
          setTimeout(() => setIntroOpen(true), 120);
        }}
        onReplayTips={() => {
          setCarLocationOpen(false);
          setTipsReplay(true);
          setTimeout(() => setTipsOpen(true), 260);
        }}
      />

      <Onboarding open={introOpen} replay={introReplay} onDone={finishIntro} />
      <CoachMarks open={tipsOpen} replay={tipsReplay} onDone={finishTips} />

      <NearbyParkingSheet
        open={nearbyOpen}
        onClose={closeNearby}
        position={nearbyPos}
        liveSpots={liveSpotsForNearby}
        onFocusSpot={focusSpot}
      />

      <GroupsSheet
        open={groupsOpen}
        onClose={() => setGroupsOpen(false)}
        userPos={geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng } : null}
        onChanged={refreshGroups}
      />

      <ReportParkingSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        pos={geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng } : null}
        onReported={() => {
          // Show the reporter their own pin straight away, on the layer where
          // other drivers can find and confirm it.
          if (!layers.community_pending) updateLayers({ ...layers, community_pending: true });
        }}
      />

      <QuickSpotSheet
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onSubmit={publishSpotted}
        pos={geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng } : null}
        busy={quickBusy}
      />

      <PrivateParkingSheet
        open={ppOpen}
        onClose={() => setPpOpen(false)}
        pos={geo.pos ? { lat: geo.pos.lat, lng: geo.pos.lng } : null}
        onChanged={refreshListings}
      />

      <PrivateListingCard listing={activeListing} onClose={() => setActiveListing(null)} />

      <Toaster theme="dark" position="top-center" />

      <style>{`
        .leaflet-container { background: #e8eef3; }
        /* Fix mobile drag: Leaflet + divIcon needs explicit touch-action off
           on the draggable marker, otherwise the browser handles touch as a
           scroll gesture and drag never starts. */
        .leaflet-marker-draggable,
        .leaflet-marker-draggable * {
          touch-action: none !important;
          -webkit-user-select: none;
          user-select: none;
        }
        .leaflet-marker-draggable { cursor: grab; }
        .leaflet-marker-draggable:active,
        .leaflet-dragging .leaflet-marker-draggable { cursor: grabbing; }
        .spotdrop-draft-icon { will-change: transform; }
        /* Give the draft pin an oversized invisible hit area for chunky fingers. */
        .spotdrop-draft-icon::before {
          content: "";
          position: absolute;
          inset: -18px;
          border-radius: 50%;
        }
        /* iOS/WebKit: small parking icons need an explicit, finger-sized hit
           area, otherwise a tap near the edge lands on the map instead. */
        .spotdrop-place-icon {
          -webkit-tap-highlight-color: transparent;
          cursor: pointer;
        }
        .spotdrop-place-icon::before {
          content: "";
          position: absolute;
          inset: -8px;
          border-radius: 12px;
        }
        .leaflet-popup-content-wrapper, .leaflet-popup-tip {
          background: #141416 !important;
          color: #fff !important;
          border-radius: 14px !important;
          border: 1px solid rgba(255,255,255,0.08) !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5) !important;
        }
        .leaflet-popup-content { margin: 12px 14px !important; font-family: 'Manrope', sans-serif !important; }
        .leaflet-popup-close-button { color: #ccc !important; }
        .leaflet-control-attribution { font-size: 9px !important; background: rgba(255,255,255,0.85) !important; color: #555 !important; }
        .leaflet-control-attribution a { color: #047857 !important; }
      `}</style>
    </div>
  );
}
